import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { z } from "zod";

const TRACKING_DISCHARGE_STATUS_VALUES = [
  "not_required",
  "discharge_requested",
  "discharge_confirmed",
  "discharge_received",
  "discharged",
] as const;

const encumbranceFormSchema = z.object({
  securityId: z.string().uuid(),
  // Accept any string for encumbranceType — the DB uses extended enum values (e.g. first_mortgage)
  // while the UI dropdown retains legacy values for simplicity. Server validates the DB schema.
  encumbranceType: z.string().min(1),
  holderType: z.string().optional().nullable(),
  holderName: z.string().optional().nullable(),
  encumbranceAmount: z.string().optional().nullable(),
  registrationDate: z.string().optional().nullable(),
  // dischargeStatus is sent as the combined field — accept both legacy and new enum values
  dischargeStatus: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
  // Discharge tracking fields
  dischargeRequired: z.boolean().default(false),
  dischargeTrackingStatus: z.enum(TRACKING_DISCHARGE_STATUS_VALUES).optional().nullable(),
  payoutFigure: z.string().optional().nullable(),
  payoutValidUntil: z.string().optional().nullable(),
  dischargeAuthoritySent: z.string().optional().nullable(),
  dischargeReceived: z.string().optional().nullable(),
});

type EncumbranceFormData = z.infer<typeof encumbranceFormSchema>;

interface EncumbranceFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  securityId: string;
  encumbrance?: any;
  onSuccess?: () => void;
}

// Determine whether the encumbrance being edited is proposed
function getIsProposed(encumbrance: any): boolean {
  return !!(encumbrance?.isProposed);
}

const ENCUMBRANCE_TYPE_OPTIONS = [
  { value: "first_mortgage", label: "First Mortgage" },
  { value: "second_mortgage", label: "Second Mortgage" },
  { value: "subsequent_mortgage", label: "Subsequent Mortgage" },
  { value: "caveat", label: "Caveat" },
  { value: "ppsr_registration", label: "PPSR Registration" },
  { value: "ato_charge", label: "ATO Charge" },
  { value: "state_revenue_charge", label: "State Revenue Charge" },
  { value: "council_charge", label: "Council Charge" },
  { value: "water_authority_charge", label: "Water Authority Charge" },
  { value: "body_corporate_charge", label: "Body Corporate Charge" },
  { value: "writ", label: "Writ" },
  { value: "court_order", label: "Court Order" },
  { value: "easement", label: "Easement" },
  { value: "covenant", label: "Covenant" },
  { value: "lease_registered", label: "Lease (Registered)" },
  { value: "option_to_purchase", label: "Option to Purchase" },
  { value: "profit_a_prendre", label: "Profit à Prendre" },
  { value: "other", label: "Other" },
];

const HOLDER_TYPE_OPTIONS = [
  { value: "bank_lender", label: "Bank Lender" },
  { value: "non_bank_lender", label: "Non-Bank Lender" },
  { value: "private_lender", label: "Private Lender" },
  { value: "ato", label: "ATO" },
  { value: "state_revenue", label: "State Revenue" },
  { value: "council", label: "Council" },
  { value: "water_authority", label: "Water Authority" },
  { value: "body_corporate", label: "Body Corporate" },
  { value: "creditor", label: "Creditor" },
  { value: "court", label: "Court" },
  { value: "our_facility", label: "Our Facility" },
];

const DISCHARGE_STATUS_OPTIONS = [
  { value: "registered", label: "Registered" },
  { value: "pending_discharge", label: "Pending Discharge" },
  { value: "discharged", label: "Discharged" },
  { value: "expired", label: "Expired" },
];

const DISCHARGE_TRACKING_STATUS_OPTIONS = [
  { value: "not_required", label: "Not Required" },
  { value: "discharge_requested", label: "Discharge Requested" },
  { value: "discharge_confirmed", label: "Discharge Confirmed" },
  { value: "discharge_received", label: "Discharge Received" },
  { value: "discharged", label: "Discharged" },
];

export function EncumbranceFormModal({
  open,
  onOpenChange,
  securityId,
  encumbrance,
  onSuccess,
}: EncumbranceFormModalProps) {
  const { toast } = useToast();
  const isEditing = !!encumbrance;
  const isProposed = getIsProposed(encumbrance);

  // When loading existing encumbrance data, the dischargeStatus from the API
  // is the DB tracking status. We initialise dischargeTrackingStatus from it.
  const existingTrackingStatus = encumbrance?.dischargeStatus &&
    TRACKING_DISCHARGE_STATUS_VALUES.includes(encumbrance.dischargeStatus as any)
    ? encumbrance.dischargeStatus
    : null;

  const form = useForm<EncumbranceFormData>({
    resolver: zodResolver(encumbranceFormSchema),
    defaultValues: {
      securityId,
      encumbranceType: encumbrance?.encumbranceType || "first_mortgage",
      holderType: encumbrance?.holderType || null,
      holderName: encumbrance?.holderName || "",
      encumbranceAmount: encumbrance?.encumbranceAmount || "",
      registrationDate: encumbrance?.registrationDate || "",
      dischargeStatus: encumbrance?.dischargeStatus || null,
      notes: encumbrance?.notes || "",
      // Discharge tracking defaults
      dischargeRequired: encumbrance?.dischargeRequired ?? false,
      dischargeTrackingStatus: existingTrackingStatus,
      payoutFigure: encumbrance?.payoutFigure || "",
      payoutValidUntil: encumbrance?.payoutValidUntil || "",
      dischargeAuthoritySent: encumbrance?.dischargeAuthoritySent || "",
      dischargeReceived: encumbrance?.dischargeReceived || "",
    },
  });

  const dischargeRequired = form.watch("dischargeRequired");

  const createMutation = useMutation({
    mutationFn: async (data: EncumbranceFormData) => {
      const endpoint = isEditing
        ? `/api/crm/securities/${securityId}/encumbrances/${encumbrance.id}`
        : `/api/crm/securities/${securityId}/encumbrances`;
      const method = isEditing ? "PATCH" : "POST";

      const { securityId: _securityId, ...updates } = data;
      const response = await apiRequest(method, endpoint, isEditing ? updates : data);
      if (!response.ok) {
        const error = await response.text();
        throw new Error(error || "Failed to save encumbrance");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/securities/${securityId}/encumbrances`] });
      queryClient.invalidateQueries({ queryKey: [`/api/crm/securities/${securityId}`] });
      toast({
        title: "Success",
        description: `Encumbrance ${isEditing ? "updated" : "created"} successfully`,
      });
      onOpenChange(false);
      form.reset();
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: EncumbranceFormData) => {
    // Map dischargeTrackingStatus → dischargeStatus on the API payload
    // (the backend column is discharge_status with the new enum values)
    // Convert empty strings to null for date/numeric fields to avoid DB errors
    const toNullIfEmpty = (v: string | null | undefined) => (v === "" || v === undefined ? null : v);
    const payload: Record<string, any> = {
      ...data,
      // Send discharge tracking status as the discharge_status field
      dischargeStatus: data.dischargeTrackingStatus || data.dischargeStatus || null,
      // Convert empty strings → null for date and numeric columns
      encumbranceAmount: toNullIfEmpty(data.encumbranceAmount),
      registrationDate: toNullIfEmpty(data.registrationDate),
      payoutFigure: toNullIfEmpty(data.payoutFigure),
      payoutValidUntil: toNullIfEmpty(data.payoutValidUntil),
      dischargeAuthoritySent: toNullIfEmpty(data.dischargeAuthoritySent),
      dischargeReceived: toNullIfEmpty(data.dischargeReceived),
      notes: toNullIfEmpty(data.notes),
    };
    // Remove the UI-only field
    delete payload.dischargeTrackingStatus;
    // For PATCH (edit mode): strip null values to prevent overwriting existing NOT NULL columns
    // Only strip nulls in edit mode — new records need to pass explicit nulls for optional fields
    if (isEditing) {
      Object.keys(payload).forEach(key => {
        if (payload[key] === null || payload[key] === undefined) {
          delete payload[key];
        }
      });
    }
    createMutation.mutate(payload as EncumbranceFormData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto" data-testid="encumbrance-form-modal">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit" : "Add"} Encumbrance</DialogTitle>
          <DialogDescription>
            {isEditing ? "Update" : "Record"} an encumbrance on this security
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="encumbranceType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Encumbrance Type</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {ENCUMBRANCE_TYPE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="dischargeStatus"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value || undefined}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {DISCHARGE_STATUS_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="holderType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Holder Type</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value || undefined}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select holder type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {HOLDER_TYPE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="holderName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Holder Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Name of encumbrance holder" {...field} value={field.value || ""} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="encumbranceAmount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Amount (AUD)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Optional"
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="registrationDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Registration Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} value={field.value || ""} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Additional encumbrance details..."
                      className="min-h-[80px]"
                      {...field}
                      value={field.value || ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Discharge Tracking Section — hidden for proposed encumbrances */}
            {!isProposed && (
              <div data-testid="discharge-tracking-section">
                <Separator className="my-2" />
                <p className="text-sm font-semibold text-foreground mb-3">Discharge Tracking</p>

                {/* dischargeRequired Switch */}
                <FormField
                  control={form.control}
                  name="dischargeRequired"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border p-3 mb-3">
                      <div className="space-y-0.5">
                        <FormLabel className="text-sm font-medium">Discharge Required</FormLabel>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          data-testid="switch-discharge-required"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {/* Conditional discharge fields shown only when dischargeRequired = true */}
                {dischargeRequired && (
                  <div className="space-y-4">
                    <FormField
                      control={form.control}
                      name="dischargeTrackingStatus"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Discharge Status</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value || undefined}
                          >
                            <FormControl>
                              <SelectTrigger data-testid="select-discharge-status">
                                <SelectValue placeholder="Select discharge status" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {DISCHARGE_TRACKING_STATUS_OPTIONS.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="payoutFigure"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Payout Figure (AUD)</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                step="0.01"
                                placeholder="Optional"
                                data-testid="input-payout-figure"
                                {...field}
                                value={field.value || ""}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="payoutValidUntil"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Payout Valid Until</FormLabel>
                            <FormControl>
                              <Input
                                type="date"
                                data-testid="input-payout-valid-until"
                                {...field}
                                value={field.value || ""}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="dischargeAuthoritySent"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Discharge Authority Sent</FormLabel>
                            <FormControl>
                              <Input
                                type="date"
                                data-testid="input-discharge-authority-sent"
                                {...field}
                                value={field.value || ""}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="dischargeReceived"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Discharge Received</FormLabel>
                            <FormControl>
                              <Input
                                type="date"
                                data-testid="input-discharge-received"
                                {...field}
                                value={field.value || ""}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={createMutation.isPending}
                data-testid="button-cancel"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending} data-testid="button-submit-encumbrance">
                {createMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {isEditing ? "Update" : "Add"} Encumbrance
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
